import React, { useState } from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

const baseData = [
  { name: "Rental Income", value: 28800, type: "income" },
  { name: "Vacancy Loss", value: -1440, type: "expense" },
  { name: "Property Mgmt", value: -2304, type: "expense" },
  { name: "Maintenance", value: -1800, type: "expense" },
  { name: "Property Tax", value: -3600, type: "expense" },
  { name: "Insurance", value: -1200, type: "expense" },
  { name: "Mortgage", value: -14400, type: "expense" },
  { name: "Tax Benefit", value: 2800, type: "benefit" },
  { name: "Appreciation", value: 18000, type: "appreciation" },
  { name: "Net Return", value: 24856, type: "net" },
];

const colorMap = {
  income: "var(--color-secondary)",
  expense: "var(--color-accent)",
  benefit: "var(--color-primary)",
  appreciation: "var(--color-warning)",
  net: "var(--color-success)",
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload?.[0]?.payload;
  return (
    <div className="bg-card border border-border rounded-xl px-4 py-3 shadow-lg">
      <p className="text-sm font-heading font-semibold text-foreground">{d?.name}</p>
      <p
        className="text-lg font-mono-data font-bold"
        style={{ color: colorMap?.[d?.type] }}
      >
        {d?.value < 0 ? "-" : "+"}${Math.abs(d?.value)?.toLocaleString()}
      </p>
    </div>
  );
};

export default function CashFlowWaterfall({ scenario }) {
  const [vacancyRate, setVacancyRate] = useState(5);
  const [expenseRatio, setExpenseRatio] = useState(35);
  const [appreciationRate, setAppreciationRate] = useState(4);

  const multiplier =
    scenario === "optimistic" ? 1.15 : scenario === "pessimistic" ? 0.82 : 1;

  const chartData = baseData?.map((d) => ({
    ...d,
    value: Math.round(d?.value * multiplier),
  }));

  return (
    <div className="card-base flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">
            Cash Flow Breakdown
          </h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">
            Annual projection with what-if scenario modeling
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(colorMap)?.map(([type, color]) => (
            <span key={type} className="flex items-center gap-1.5 text-xs font-caption text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
              {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
            </span>
          ))}
        </div>
      </div>
      <div className="w-full h-64 md:h-72" aria-label="Cash Flow Waterfall Chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)", fontFamily: "Inter" }}
              angle={-35}
              textAnchor="end"
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => `$${(v / 1000)?.toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeWidth={2} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {chartData?.map((entry) => (
                <Cell key={entry?.name} fill={colorMap?.[entry?.type]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* What-if sliders */}
      <div className="border-t border-border pt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide">
            Vacancy Rate: <span className="text-primary font-semibold">{vacancyRate}%</span>
          </label>
          <input
            type="range" min={0} max={20} value={vacancyRate}
            onChange={(e) => setVacancyRate(Number(e?.target?.value))}
            className="w-full accent-primary h-2 rounded-full cursor-pointer"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide">
            Expense Ratio: <span className="text-primary font-semibold">{expenseRatio}%</span>
          </label>
          <input
            type="range" min={20} max={60} value={expenseRatio}
            onChange={(e) => setExpenseRatio(Number(e?.target?.value))}
            className="w-full accent-primary h-2 rounded-full cursor-pointer"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide">
            Appreciation: <span className="text-primary font-semibold">{appreciationRate}%</span>
          </label>
          <input
            type="range" min={0} max={12} step={0.5} value={appreciationRate}
            onChange={(e) => setAppreciationRate(Number(e?.target?.value))}
            className="w-full accent-primary h-2 rounded-full cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
}